// client/src/pages/pmPortal/OwnerOverviewPage.js
import React, { useEffect, useState } from 'react';
import { usePmPortalApiClient } from '../../hooks/usePmPortalApiClient';
import { PageHeader, Panel, Stat, Table, Loading, Notice, money, fmtDate, titleCase, Pill, statusTone }
    from '../../components/secure/pm/PMKit';

const OwnerOverviewPage = () => {
    const api = usePmPortalApiClient();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let live = true;
        api.get('/api/pm-portal/owner/summary')
            .then(d => { if (live) setData(d); })
            .catch(e => { if (live) setError(e.message); });
        return () => { live = false; };
    }, [api]);

    if (error) return <Notice tone="bad" title="Could not load your overview" message={error} />;
    if (!data) return <Loading />;

    const units = data.properties.reduce((s, p) => s + Number(p.unit_count || 0), 0);
    const occupied = data.properties.reduce((s, p) => s + Number(p.occupied_count || 0), 0);

    return (
        <>
            <PageHeader
                title="Your portfolio"
                subtitle="Figures are drawn from your own set of books. Rent collected for you is held in a special account and is not brokerage income."
            />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <Stat label="Cash in your book" value={money(data.cash_position)} />
                <Stat label="Properties" value={data.properties.length} />
                <Stat
                    label="Units occupied"
                    value={units ? `${occupied} of ${units}` : '—'}
                    sub={units ? `${Math.round((occupied / units) * 100)}% occupancy` : null}
                />
            </div>

            <Panel title="Properties">
                <Table
                    columns={[
                        { key: 'property_name', label: 'Property' },
                        { key: 'street_address', label: 'Address',
                          render: r => [r.street_address, r.city, r.state].filter(Boolean).join(', ') },
                        { key: 'asset_class', label: 'Class', render: r => titleCase(r.asset_class) },
                        { key: 'unit_count', label: 'Units', align: 'right' },
                        { key: 'occupied_count', label: 'Occupied', align: 'right' },
                        { key: 'ownership_percent', label: 'Your share', align: 'right',
                          render: r => `${Number(r.ownership_percent).toFixed(2)}%` },
                    ]}
                    rows={data.properties}
                    empty="No properties are recorded against your account yet."
                />
            </Panel>

            <Panel title="Books" subtitle="A set of books per ownership entity. Each one balances on its own.">
                <Table
                    columns={[
                        { key: 'book_no', label: 'Book' },
                        { key: 'opened_on', label: 'Opened', render: r => fmtDate(r.opened_on) },
                        { key: 'status', label: 'Status', render: r => <Pill tone={statusTone(r.status)}>{titleCase(r.status)}</Pill> },
                        { key: 'reserve_floor', label: 'Reserve held back', align: 'right', render: r => money(r.reserve_floor) },
                    ]}
                    rows={data.books}
                    empty="No book has been opened yet."
                />
            </Panel>
        </>
    );
};

export default OwnerOverviewPage;
